import { useTranslation } from "react-i18next";
import { errorCodeOf } from "../../api/fetcher";
import { useBooks } from "../../api/hooks";
import { Banner } from "../../components/feedback/Banner";
import { EmptyState } from "../../components/feedback/EmptyState";
import { ErrorState } from "../../components/feedback/ErrorState";
import { Skeleton } from "../../components/feedback/Skeleton";
import { BookCard } from "./BookCard";

/** The right half of the dashboard: one card per Book, or the empty state that points at the drop zone. */
export function BookList() {
  const { t } = useTranslation("dashboard");
  const books = useBooks();
  // TanStack Query keeps the last payload when a refetch fails, so the list is
  // still on screen: saying it could not be loaded on top of the cards it just
  // loaded is the one thing this must not do. No cards, no data: then the
  // error state is the whole answer.
  const cards = books.data?.books;
  const failedOnLoad = books.isError && cards === undefined;
  const failedOnRefresh = books.isError && cards !== undefined;

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <p className="label-mono">{t("list.label")}</p>
      {books.isPending ? (
        <>
          <Skeleton variant="row" />
          <Skeleton variant="row" />
        </>
      ) : null}
      {failedOnLoad ? (
        <ErrorState
          code={errorCodeOf(books.error)}
          onRetry={() => void books.refetch()}
          busy={books.isFetching}
        >
          {t("list.loadError")}
        </ErrorState>
      ) : null}
      {failedOnRefresh ? <Banner tone="warn">{t("list.refreshError")}</Banner> : null}
      {cards?.length === 0 ? (
        <EmptyState title={t("list.emptyTitle")}>{t("list.emptyBody")}</EmptyState>
      ) : null}
      {cards?.map((book) => (
        <BookCard key={book.id} book={book} />
      ))}
    </section>
  );
}
